import type { NavigateFunction } from "react-router-dom";
import type { DashboardVariant } from "./dashboardVariant";

/** Canonical dashboard instrumentation event names (Phase 0 Track B). */
export const DASHBOARD_EVENTS = {
  VIEW: "dashboard_view",
  RESUME_SEARCH_CLICK: "dashboard_resume_search_click",
  RESUME_SEARCH_DISMISS: "dashboard_resume_search_dismiss",
  HEADER_SEARCH_CLICK: "dashboard_header_search_click",
  CASE_LINKED_SEARCH: "dashboard_case_linked_search",
  COMPARE_FROM_FAVORITES: "dashboard_compare_from_favorites",
  CONTACT_FROM_FAVORITES: "dashboard_contact_from_favorites",
  SAVE_STATUS_UPDATED: "dashboard_save_status_updated",
  FAVORITE_NOTE_SAVED: "dashboard_favorite_note_saved",
  PATIENT_TODAY_CTA_CLICK: "dashboard_patient_today_cta_click",
  COMPARE_LAST_OPENED: "dashboard_compare_last_opened",
  EXPORT_DECISION_SUMMARY: "dashboard_export_decision_summary",
  GUIDED_INTAKE_START: "dashboard_guided_intake_start",
  COMPARE_WORKSHEET_COMPLETED: "dashboard_compare_worksheet_completed",
  FAVORITES_LOADED: "dashboard_favorites_loaded",
  MANAGE_WIDGETS_OPEN: "dashboard_manage_widgets_open",
  YOUR_SEARCHES_TAB: "dashboard_your_searches_tab",
  REFERENCE_SCROLL_CLICK: "dashboard_reference_scroll_click",
  QUICK_SEARCH: "dashboard_quick_search",
  VIEW_PROVIDER: "dashboard_view_provider",
} as const;

export type DashboardEventName =
  (typeof DASHBOARD_EVENTS)[keyof typeof DASHBOARD_EVENTS];

/** Resume banner subject: case-linked (SW) or care journey (patient). */
export type DashboardResumeContextType = "case" | "journey";

export type DashboardEventPayload = Record<
  string,
  string | number | boolean | null | undefined
>;

export type DashboardBaselineSnapshot = {
  capturedAt: string;
  variant: DashboardVariant;
  layoutVersion: "zones_v1";
  mergedSearches: boolean;
  visibleWidgetIds: string[];
  heroWidgetIds: string[];
  primaryWidgetIds: string[];
  referenceWidgetIds: string[];
  resumeBannerEligible: boolean;
};

const BASELINE_SESSION_KEY = "dashboard_baseline_captured_v1";
const EVENT_COUNTER_KEY = "dashboard_event_counters_v1";
/** Base key; per-user keys are `${prefix}:u{id}` or `${prefix}:u{id}:j{journeyId}`. */
export const COMPARE_LAST_OPENED_KEY_PREFIX = "dashboard_compare_last_opened_v1";
/** @deprecated Unscoped legacy key — cleared on write/logout. */
const COMPARE_LAST_OPENED_LEGACY_KEY = COMPARE_LAST_OPENED_KEY_PREFIX;
const GUIDED_INTAKE_DISMISSED_KEY = "patient_guided_intake_dismissed_v1";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures.
  }
}

function incrementLocalEventCounter(eventName: DashboardEventName): void {
  try {
    const raw = sessionStorage.getItem(EVENT_COUNTER_KEY);
    const counters = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    counters[eventName] = (counters[eventName] ?? 0) + 1;
    sessionStorage.setItem(EVENT_COUNTER_KEY, JSON.stringify(counters));
  } catch {
    // Ignore storage failures.
  }
}

/** Fire a dashboard event to GA (when available) and local session counters. */
export function trackDashboardEvent(
  eventName: DashboardEventName,
  payload: DashboardEventPayload = {}
): void {
  incrementLocalEventCounter(eventName);

  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      event_category: "dashboard",
      ...payload,
      timestamp: Date.now(),
    });
  }
}

/** Read session event counters (for QA / baseline review in devtools). */
export function getDashboardEventCounters(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(EVENT_COUNTER_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** Capture one baseline snapshot per dashboard session for Phase 0 metrics. */
export function captureDashboardBaseline(
  snapshot: DashboardBaselineSnapshot
): boolean {
  if (safeSessionGet(BASELINE_SESSION_KEY) === "1") {
    return false;
  }
  safeSessionSet(BASELINE_SESSION_KEY, "1");
  trackDashboardEvent(DASHBOARD_EVENTS.VIEW, {
    variant: snapshot.variant,
    layout_version: snapshot.layoutVersion,
    merged_searches: snapshot.mergedSearches,
    visible_widget_count: snapshot.visibleWidgetIds.length,
    resume_banner_eligible: snapshot.resumeBannerEligible,
    visible_widgets: snapshot.visibleWidgetIds.join(","),
  });
  return true;
}

export function trackDashboardHeaderSearch(
  variant: DashboardVariant,
  options?: { contextualCase?: boolean; patientInitials?: string }
): void {
  trackDashboardEvent(
    options?.contextualCase
      ? DASHBOARD_EVENTS.CASE_LINKED_SEARCH
      : DASHBOARD_EVENTS.HEADER_SEARCH_CLICK,
    {
      variant,
      contextual_case: options?.contextualCase ?? false,
      patient_initials: options?.patientInitials,
    }
  );
}

export function trackDashboardResumeSearch(
  variant: DashboardVariant,
  options?: {
    /** @deprecated Use hasResumeSubject */
    hasCaseContext?: boolean;
    hasResumeSubject?: boolean;
    subjectLabel?: string;
    contextId?: string;
    contextType?: DashboardResumeContextType;
  }
): void {
  const hasResumeSubject =
    options?.hasResumeSubject ?? options?.hasCaseContext ?? false;
  trackDashboardEvent(DASHBOARD_EVENTS.RESUME_SEARCH_CLICK, {
    variant,
    has_resume_subject: hasResumeSubject,
    has_case_context: hasResumeSubject,
    subject_label: options?.subjectLabel,
    context_id: options?.contextId,
    context_type: options?.contextType,
  });
}

export function trackDashboardResumeDismiss(variant: DashboardVariant): void {
  trackDashboardEvent(DASHBOARD_EVENTS.RESUME_SEARCH_DISMISS, { variant });
}

export function trackDashboardYourSearchesTab(
  variant: DashboardVariant,
  tab: "recent" | "saved"
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.YOUR_SEARCHES_TAB, {
    variant,
    tab,
  });
}

export function trackDashboardReferenceScroll(
  variant: DashboardVariant,
  options?: { referenceLabel?: string }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.REFERENCE_SCROLL_CLICK, {
    variant,
    reference_label: options?.referenceLabel,
  });
}

export function trackDashboardCompareFromFavorites(
  variant: DashboardVariant,
  providerCount: number,
  options?: {
    userId?: string | number | null;
    journeyId?: string | number | null;
  }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.COMPARE_FROM_FAVORITES, {
    variant,
    provider_count: providerCount,
  });
  recordDashboardCompareLastOpened(options);
  trackDashboardEvent(DASHBOARD_EVENTS.COMPARE_LAST_OPENED, {
    variant,
  });
}

export function trackDashboardContactFromFavorites(
  variant: DashboardVariant,
  options?: { providerId?: string | number }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.CONTACT_FROM_FAVORITES, {
    variant,
    provider_id: options?.providerId,
  });
}

export function trackDashboardSaveStatusUpdated(
  variant: DashboardVariant,
  options: {
    providerId?: string | number;
    decisionStatus: string;
    previousStatus?: string;
  }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.SAVE_STATUS_UPDATED, {
    variant,
    provider_id: options.providerId,
    decision_status: options.decisionStatus,
    previous_status: options.previousStatus,
  });
}

export function trackDashboardFavoriteNoteSaved(
  variant: DashboardVariant,
  options?: { providerId?: string | number; hasNote?: boolean }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.FAVORITE_NOTE_SAVED, {
    variant,
    provider_id: options?.providerId,
    has_note: options?.hasNote ?? true,
  });
}

export type PatientTodayCta =
  | "call"
  | "update_status"
  | "view"
  | "compare";

export type PatientTodayQueue =
  | "follow_up"
  | "not_contacted"
  | "ready_to_compare";

export function trackDashboardPatientTodayCtaClick(
  options: {
    cta: PatientTodayCta;
    queue: PatientTodayQueue;
    providerId?: string | number;
  }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.PATIENT_TODAY_CTA_CLICK, {
    variant: "patient",
    cta: options.cta,
    queue: options.queue,
    provider_id: options.providerId,
  });
}

export function trackDashboardExportDecisionSummary(options?: {
  providerCount?: number;
}): void {
  trackDashboardEvent(DASHBOARD_EVENTS.EXPORT_DECISION_SUMMARY, {
    variant: "patient",
    provider_count: options?.providerCount,
  });
}

export function trackDashboardGuidedIntakeStart(
  options: {
    careTypeSearchType: string;
    urgency: "exploring" | "soon";
    forWhom?: string;
  }
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.GUIDED_INTAKE_START, {
    variant: "patient",
    care_type: options.careTypeSearchType,
    urgency: options.urgency,
    ...(options.forWhom ? { for_whom: options.forWhom } : {}),
  });
}

export function trackDashboardCompareWorksheetCompleted(options: {
  selectedCount: number;
  updatedCount: number;
  missingCount: number;
}): void {
  trackDashboardEvent(DASHBOARD_EVENTS.COMPARE_WORKSHEET_COMPLETED, {
    variant: "patient",
    selected_count: options.selectedCount,
    updated_count: options.updatedCount,
    missing_count: options.missingCount,
  });
}

export function trackDashboardFavoritesLoaded(
  variant: DashboardVariant,
  total: number
): void {
  trackDashboardEvent(DASHBOARD_EVENTS.FAVORITES_LOADED, {
    variant,
    favorites_total: total,
  });
}

function resolveAuthUserId(): string | number | null {
  try {
    // Lazy require avoids circular imports at module evaluation.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useAuthStore } = require("../stores/authStore") as typeof import("../stores/authStore");
    return useAuthStore.getState().user?.id ?? null;
  } catch {
    return null;
  }
}

export function buildDashboardCompareLastOpenedKey(options?: {
  userId?: string | number | null;
  journeyId?: string | number | null;
}): string {
  const userId = options?.userId ?? resolveAuthUserId();
  if (userId == null || userId === "") {
    return COMPARE_LAST_OPENED_KEY_PREFIX;
  }
  const journeyId = options?.journeyId;
  if (journeyId != null && journeyId !== "") {
    return `${COMPARE_LAST_OPENED_KEY_PREFIX}:u${userId}:journey:${journeyId}`;
  }
  return `${COMPARE_LAST_OPENED_KEY_PREFIX}:u${userId}`;
}

/** Persist last compare open time for Phase C “Ready to compare” queue. */
export function recordDashboardCompareLastOpened(options?: {
  userId?: string | number | null;
  journeyId?: string | number | null;
}): void {
  try {
    const key = buildDashboardCompareLastOpenedKey(options);
    localStorage.setItem(key, new Date().toISOString());
    // Stop leaking across accounts on shared devices.
    if (key !== COMPARE_LAST_OPENED_LEGACY_KEY) {
      localStorage.removeItem(COMPARE_LAST_OPENED_LEGACY_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}

export function getDashboardCompareLastOpenedAt(options?: {
  userId?: string | number | null;
  journeyId?: string | number | null;
}): string | null {
  try {
    const key = buildDashboardCompareLastOpenedKey(options);
    const scoped = localStorage.getItem(key);
    if (scoped) {
      return scoped;
    }
    // Unscoped legacy only when no user context (tests / anonymous).
    const userId = options?.userId ?? resolveAuthUserId();
    if (userId == null || userId === "") {
      return localStorage.getItem(COMPARE_LAST_OPENED_LEGACY_KEY);
    }
    return null;
  } catch {
    return null;
  }
}

/** Clear all compare-last-opened keys (logout / account switch). */
export function clearDashboardCompareLastOpenedStorage(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key === COMPARE_LAST_OPENED_KEY_PREFIX ||
        key?.startsWith(`${COMPARE_LAST_OPENED_KEY_PREFIX}:`)
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore storage failures.
  }
}

export function getDashboardPhaseABaselineReport(): {
  capturedAt: string;
  eventCounters: Record<string, number>;
  compareLastOpenedAt: string | null;
} {
  return {
    capturedAt: new Date().toISOString(),
    eventCounters: getDashboardEventCounters(),
    compareLastOpenedAt: getDashboardCompareLastOpenedAt(),
  };
}

export function trackDashboardManageWidgetsOpen(variant: DashboardVariant): void {
  trackDashboardEvent(DASHBOARD_EVENTS.MANAGE_WIDGETS_OPEN, { variant });
}

/** @internal test helper */
export function resetDashboardInstrumentationForTests(): void {
  try {
    sessionStorage.removeItem(BASELINE_SESSION_KEY);
    sessionStorage.removeItem(EVENT_COUNTER_KEY);
    clearDashboardCompareLastOpenedStorage();
    // Clear legacy banner-dismiss key if present from older builds.
    localStorage.removeItem(GUIDED_INTAKE_DISMISSED_KEY);
  } catch {
    // Ignore.
  }
}

/** No-op navigate helper type export for consumers that track then navigate. */
export type DashboardNavigate = NavigateFunction;
